"use client";

import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  useToast,
  Spinner,
  IconButton,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Flex,
  Progress,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon, DownloadIcon, DeleteIcon, AddIcon, RepeatIcon, ViewIcon } from '@chakra-ui/icons';
import documentVerificationService from 'services/Recruit/services/documentVerification';
import S3Helper from 'common/utils/s3-helper';
import TextEditor from 'common/components/textEditor/TextEditor';
import { getStatusBadgeStyle } from 'common/utils/statusColor';
import CustomDatePicker from 'common/components/datePicker/CustomDatePicker';
import { getApi } from 'common/services/api';
import { API_PATHS } from 'common/services/apiPaths';

const DocumentVerificationTab = ({ candidateId, candidateName, applicationId: propApplicationId, isReadOnly = false }) => {
  // Support both candidateId and applicationId for backward compatibility
  const [applicationId, setApplicationId] = useState(propApplicationId);
  const [docVerification, setDocVerification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState('personal');
  const [selectedDocType, setSelectedDocType] = useState('');
  const toast = useToast();

  // Get current user role - use useState to ensure it updates
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [isRecruiter, setIsRecruiter] = useState(false);
  const [isReviewer, setIsReviewer] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Check if flow is completed (approved)
  const isFlowCompleted = docVerification?.verificationStatus === 'approved';
  // Disable actions if read only (unless superAdmin), flow is completed
  const shouldDisableActions = (isReadOnly && !isSuperAdmin) || isFlowCompleted;

  useEffect(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        // Check activeRoleId first, then fall back to role
        const role = user?.activeRoleId?.roleName || user?.role || null;
        setCurrentUserRole(role);
        // Allow recruiter, hiring manager, and superAdmin to upload documents
        // Check if user has recruiter or hiringManager in their roles array
        const hasRecruiterRole = user?.roles?.some(r => {
          const roleName = typeof r === 'object' ? r.roleName : r;
          return roleName === 'recruiter' || roleName === 'hiringManager';
        });
        const superAdminCheck = role === 'superAdmin' || role === 'admin';
        setIsSuperAdmin(superAdminCheck);
        
        const recruiterCheck = role === 'recruiter' || role === 'hiringManager' || superAdminCheck || hasRecruiterRole;
        setIsRecruiter(recruiterCheck);
        // Check if user has reviewer role - check activeRoleId, role field, and roles array
        const hasReviewerRole = user?.roles?.some(r =>
          (typeof r === 'object' ? r.roleName : r) === 'reviewer'
        );
        const reviewerCheck = role === 'reviewer' || superAdminCheck || hasReviewerRole;
        setIsReviewer(reviewerCheck);

        console.log('[Document Verification] Role detection:', {
          role,
          activeRoleId: user?.activeRoleId,
          userRole: user?.role,
          roles: user?.roles,
          hasRecruiterRole,
          hasReviewerRole,
          isRecruiter: recruiterCheck,
          isReviewer: reviewerCheck,
          isSuperAdmin: superAdminCheck,
          user: user
        });
      } else {
        console.log('[Document Verification] No user data in localStorage');
      }
    } catch (error) {
      console.error('Error getting user role:', error);
    }
  }, []);

  // Modal state management - using useState for reliability
  const [isRequestOpen, setIsRequestOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteDocInfo, setDeleteDocInfo] = useState({ docId: null, category: null });
  
  // Accordion state management - track which accordion items are open
  const [expandedAccordionIndex, setExpandedAccordionIndex] = useState([]);
  
  // Document viewer modal state
  const [viewerDocument, setViewerDocument] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerLoading, setViewerLoading] = useState(false);

  // Modal handlers - always defined as functions
  const onRequestOpen = function () { setIsRequestOpen(true); };
  const onRequestClose = function () { setIsRequestOpen(false); };
  const onUploadOpen = function () { setIsUploadOpen(true); };
  const onUploadClose = function () { setIsUploadOpen(false); };
  const onStatusOpen = function () { setIsStatusOpen(true); };
  const onStatusClose = function () { setIsStatusOpen(false); };
  const onReviewOpen = function () { setIsReviewOpen(true); };
  const onReviewClose = function () { setIsReviewOpen(false); };

  const [requestForm, setRequestForm] = useState({
    emailSubject: 'Document Verification Request',
    emailContent: '',
    requiredDocuments: [],
    deadlineDate: '',
  });

  const [statusForm, setStatusForm] = useState({
    status: 'accepted',
    comments: '',
    category: 'personal',
    docId: null,
    documentType: '',
    fileName: '',
  });

  const [reviewForm, setReviewForm] = useState({
    status: 'approved',
    comments: '',
    reviewType: 'admin',
  });

  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const s3HelperInstance = S3Helper.getInstance();

  const documentTypes = {
    personal: ['Aadhar Card', 'PAN Card', 'Passport', 'Voter ID', 'Driving License'],
    professional: ['Resume', 'Experience Letter', 'Education Certificate', 'Salary Slip', 'Offer Letter'],
  };

  const statusColors = {
    pending: 'yellow',
    submitted_for_review: 'purple',
    accepted: 'green',
    rejected: 'red',
    needs_info: 'orange',
  };

  // Helper function to format status for display
  const formatStatus = (status) => {
    const statusMap = {
      pending: 'Pending',
      submitted_for_review: 'Submitted for Review',
      accepted: 'Approved',
      rejected: 'Rejected',
      needs_info: 'Needs More Information',
    };
    return statusMap[status] || status;
  };

  // Find application from candidateId if not provided
  useEffect(() => {
    const findApplication = async () => {
      if (propApplicationId) {
        setApplicationId(propApplicationId);
        return;
      }

      if (candidateId && !propApplicationId) {
        try {
          // Ensure candidateId is a string
          const candidateIdStr = typeof candidateId === 'string' ? candidateId : (candidateId?._id || candidateId?.toString() || String(candidateId));
          if (!candidateIdStr || candidateIdStr === '[object Object]') {
            console.error('Invalid candidateId:', candidateId);
            return;
          }

          // Check if user is reviewer by reading from localStorage directly
          let isUserReviewer = false;
          try {
            const userData = localStorage.getItem('user');
            if (userData) {
              const user = JSON.parse(userData);
              const role = user?.activeRoleId?.roleName || user?.role || null;
              const hasReviewerRole = user?.roles?.some(r =>
                (typeof r === 'object' ? r.roleName : r) === 'reviewer'
              );
              isUserReviewer = role === 'reviewer' || hasReviewerRole;
            }
          } catch (e) {
            console.error('Error checking reviewer role:', e);
          }

          // For reviewers, use getDocumentVerificationByCandidate instead of fetching applications
          if (isUserReviewer) {
            try {
              const response = await documentVerificationService.getDocumentVerificationByCandidate(candidateIdStr);
              const docVerification = response?.data?.data;
              if (docVerification?.application?._id) {
                setApplicationId(docVerification.application._id);
                // Also set the doc verification directly to avoid another API call
                setDocVerification(docVerification);
              }
            } catch (error) {
              // 404 is expected if document verification doesn't exist yet
              if (error.response?.status !== 404) {
                console.error('Error fetching document verification for reviewer:', error);
              }
            }
            return;
          }

          // For non-reviewers, get the latest application for this candidate
          const response = await getApi(`${API_PATHS.CANDIDATE_LIST}${candidateIdStr}/applications`);
          const applications = response?.data?.data || [];
          if (applications.length > 0) {
            // Use the most recent application
            const latestApp = applications.sort((a, b) =>
              new Date(b.createdAt) - new Date(a.createdAt)
            )[0];
            setApplicationId(latestApp._id);
          }
        } catch (error) {
          console.error('Error fetching applications for candidate:', error);
          // Don't show toast for reviewers as they don't have access to applications
          try {
            const userData = localStorage.getItem('user');
            if (userData) {
              const user = JSON.parse(userData);
              const role = user?.activeRoleId?.roleName || user?.role || null;
              const hasReviewerRole = user?.roles?.some(r =>
                (typeof r === 'object' ? r.roleName : r) === 'reviewer'
              );
              const isUserReviewer = role === 'reviewer' || hasReviewerRole;
              if (!isUserReviewer && error.response?.status !== 404) {
                toast({
                  title: 'Error',
                  description: 'Failed to load applications',
                  status: 'error',
                  duration: 3000,
                });
              }
            }
          } catch (e) {
            // If we can't check role, show error anyway
            if (error.response?.status !== 404) {
              toast({
                title: 'Error',
                description: 'Failed to load applications',
                status: 'error',
                duration: 3000,
              });
            }
          }
        }
      }
    };

    findApplication();
  }, [candidateId, propApplicationId]);

  useEffect(() => {
    if (applicationId) {
      fetchDocumentVerification();
    } else {
      setDocVerification(null);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const fetchDocumentVerification = async () => {
    if (!applicationId) return;

    setIsLoading(true);
    try {
      const response = await documentVerificationService.getDocumentVerification(applicationId);
      const updatedData = response.data?.data;
      console.log('[fetchDocumentVerification] Fetched data:', {
        verificationStatus: updatedData?.verificationStatus,
        personalDocs: updatedData?.documents?.personal?.map(doc => ({
          id: doc._id,
          type: doc.documentType,
          status: doc.status,
          reviewedBy: doc.reviewedBy ? `${doc.reviewedBy.firstName} ${doc.reviewedBy.lastName}` : null
        })),
        professionalDocs: updatedData?.documents?.professional?.map(doc => ({
          id: doc._id,
          type: doc.documentType,
          status: doc.status,
          reviewedBy: doc.reviewedBy ? `${doc.reviewedBy.firstName} ${doc.reviewedBy.lastName}` : null
        }))
      });
      setDocVerification(updatedData);
    } catch (error) {
      // 404 is expected if document verification doesn't exist yet
      if (error.response?.status === 404) {
        setDocVerification(null);
      } else {
        console.error('Error fetching document verification:', error);
        // Only show toast for non-404 errors
        if (error.response?.status !== 404) {
          toast({
            title: 'Error',
            description: error.response?.data?.message || 'Failed to fetch document verification',
            status: 'error',
            duration: 3000,
          });
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestDocuments = async () => {
    if (!applicationId) {
      toast({
        title: 'Error',
        description: 'Application ID is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!requestForm.emailSubject || !requestForm.emailContent) {
      toast({
        title: 'Error',
        description: 'Please fill in email subject and content',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await documentVerificationService.requestDocuments({
        applicationId,
        emailSubject: requestForm.emailSubject,
        emailContent: requestForm.emailContent,
        requiredDocuments: requestForm.requiredDocuments || [],
        deadlineDate: requestForm.deadlineDate || undefined,
      });

      toast({
        title: 'Success',
        description: response?.data?.message || 'Document request email sent successfully',
        status: 'success',
        duration: 3000,
      });
      onRequestClose();
      // Reset form
      setRequestForm({
        emailSubject: 'Document Verification Request',
        emailContent: '',
        requiredDocuments: [],
        deadlineDate: '',
      });
      fetchDocumentVerification();
    } catch (error) {
      console.error('Error requesting documents:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.message || 'Failed to send document request',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile || !selectedDocType) {
      toast({
        title: 'Error',
        description: 'Please select a document type and file',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Normalize content type - detect from file extension if file.type is empty
      let normalizedContentType = uploadFile.type;

      // If file.type is empty, try to detect from file extension
      if (!normalizedContentType || normalizedContentType === '') {
        const ext = uploadFile.name.split('.').pop()?.toLowerCase();
        const mimeTypes = {
          'png': 'image/png',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'pdf': 'application/pdf',
          'doc': 'application/msword',
          'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        };
        normalizedContentType = mimeTypes[ext] || 'application/octet-stream';
        console.log('[Upload] Detected Content-Type from extension:', { ext, contentType: normalizedContentType });
      }

      // Normalize: trim whitespace and ensure it's not empty
      normalizedContentType = (normalizedContentType && normalizedContentType.trim()) || 'application/octet-stream';

      console.log('[Upload Step 1] Requesting presigned URL with:', {
        fileName: uploadFile.name,
        contentType: normalizedContentType,
        fileSize: uploadFile.size,
        category: selectedCategory,
        originalFileType: uploadFile.type
      });

      // Step 1: Get presigned URL from backend
      const presignedResponse = await documentVerificationService.getUploadPresignedUrl({
        applicationId,
        fileName: uploadFile.name,
        contentType: normalizedContentType, // Send the normalized content type
        category: selectedCategory,
      });

      const responseData = presignedResponse.data?.data || presignedResponse.data;
      const { uploadUrl, s3Key, fileName: s3FileName, originalFileName, contentType: presignedContentType } = responseData;

      console.log('[Upload Step 2] Presigned URL response:', {
        uploadUrl: uploadUrl?.substring(0, 100) + '...',
        contentType: presignedContentType,
        s3Key,
        fullResponse: responseData
      });

      // Step 2: Upload file to S3 using presigned URL
      // CRITICAL: Use the EXACT content type that was returned from backend
      // The backend returns the contentType it used to generate the presigned URL
      // We MUST use that exact value (normalized/trimmed), or use what we sent if backend didn't return it
      const returnedContentType = presignedContentType ? presignedContentType.trim() : null;
      const contentTypeToUse = returnedContentType || normalizedContentType;

      console.log('[Upload Step 3] Content-Type matching:', {
        sentToBackend: normalizedContentType,
        returnedFromBackend: presignedContentType,
        normalizedReturned: returnedContentType,
        willUseInPUT: contentTypeToUse,
        exactMatch: returnedContentType === normalizedContentType ? 'EXACT MATCH' : 'MISMATCH',
        bothNormalized: returnedContentType && normalizedContentType && returnedContentType === normalizedContentType
      });

      if (returnedContentType && returnedContentType !== normalizedContentType) {
        console.error('[Upload Step 3] ERROR: Content-Type mismatch!', {
          sent: normalizedContentType,
          returned: presignedContentType,
          normalizedReturned: returnedContentType,
          willUse: contentTypeToUse,
          warning: 'This will cause 400 error if not matching'
        });
      }

      await s3HelperInstance.updatedUploadToS3(
        uploadFile,
        uploadUrl,
        (progress) => {
          setUploadProgress(progress);
        },
        contentTypeToUse // Use the exact content type from presigned URL response
      );

      // Step 3: Save document metadata to database
      await documentVerificationService.saveDocumentMetadata({
        applicationId,
        documentType: selectedDocType,
        category: selectedCategory,
        s3Key: s3Key,
        fileName: s3FileName,
        originalFileName: originalFileName || uploadFile.name,
        fileSize: uploadFile.size,
      });

      toast({
        title: 'Success',
        description: 'Document uploaded successfully',
        status: 'success',
        duration: 3000,
      });
      onUploadClose();
      setUploadFile(null);
      setSelectedDocType('');
      setUploadProgress(0);
      fetchDocumentVerification();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.message || 'Failed to upload document',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleUpdateStatus = async () => {
    // Validate that status is selected
    if (!statusForm.status || statusForm.status === '' || statusForm.status === 'submitted_for_review') {
      toast({
        title: 'Error',
        description: 'Please select a valid status (Accept or Reject)',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Validate that docId is present
    if (!statusForm.docId) {
      toast({
        title: 'Error',
        description: 'Document ID is missing',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      console.log('[handleUpdateStatus] Updating status with:', {
        verificationId: docVerification._id,
        docId: statusForm.docId,
        status: statusForm.status,
        comments: statusForm.comments,
        category: statusForm.category,
        documentType: statusForm.documentType,
        fileName: statusForm.fileName,
      });

      const response = await documentVerificationService.updateDocumentStatus(
        docVerification._id,
        statusForm.docId,
        {
          status: statusForm.status,
          comments: statusForm.comments,
          category: statusForm.category,
        }
      );

      console.log('[handleUpdateStatus] Response:', response);
      console.log('[handleUpdateStatus] Updated document status from response:', response?.data?.data?.status);
      console.log('[handleUpdateStatus] Overall verification status from response:', response?.data?.verificationStatus);
      console.log('[handleUpdateStatus] Status form status:', statusForm.status);

      // Verify the status was updated correctly
      if (response?.data?.data?.status !== statusForm.status) {
        console.warn('[handleUpdateStatus] Status mismatch! Response status:', response?.data?.data?.status, 'Expected:', statusForm.status);
      }

      // Log if overall verification status changed
      if (response?.data?.verificationStatus) {
        console.log('[handleUpdateStatus] Overall verification status updated to:', response?.data?.verificationStatus);
      }

      toast({
        title: 'Success',
        description: `Document "${statusForm.documentType || 'document'}" status updated to ${formatStatus(statusForm.status)}`,
        status: 'success',
        duration: 3000,
      });
      onStatusClose();
      // Reset status form
      setStatusForm({
        status: 'accepted',
        comments: '',
        category: 'personal',
        docId: null,
        documentType: '',
        fileName: '',
      });

      // Small delay to ensure backend transaction is committed, then fetch updated data
      console.log('[handleUpdateStatus] Waiting 500ms before fetching updated document verification...');
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('[handleUpdateStatus] Fetching updated document verification...');
      await fetchDocumentVerification();
      console.log('[handleUpdateStatus] Document verification refreshed');
    } catch (error) {
      console.error('[handleUpdateStatus] Error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update document status',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleSubmitForReview = async (docId = null, category = null) => {
    try {
      const submitData = {
        reviewType: reviewForm.reviewType || 'admin',
      };

      // If docId and category are provided, submit specific document
      if (docId && category) {
        submitData.docId = docId;
        submitData.category = category;
      }

      await documentVerificationService.submitForReview(docVerification._id, submitData);
      toast({
        title: 'Success',
        description: docId ? 'Document submitted for review successfully' : 'Documents submitted for review successfully',
        status: 'success',
        duration: 3000,
      });
      fetchDocumentVerification();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to submit for review',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleReview = async () => {
    try {
      await documentVerificationService.reviewDocuments(docVerification._id, {
        status: reviewForm.status,
        comments: reviewForm.comments,
      });
      toast({
        title: 'Success',
        description: `Document verification ${reviewForm.status} successfully`,
        status: 'success',
        duration: 3000,
      });
      onReviewClose();
      fetchDocumentVerification();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to review documents',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleDeleteDocumentClick = (docId, category) => {
    setDeleteDocInfo({ docId, category });
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteDocument = async () => {
    const { docId, category } = deleteDocInfo;
    setIsDeleteConfirmOpen(false);

    try {
      await documentVerificationService.deleteDocument(docVerification._id, docId, category);
      toast({
        title: 'Success',
        description: 'Document deleted successfully',
        status: 'success',
        duration: 3000,
      });
      fetchDocumentVerification();
      setDeleteDocInfo({ docId: null, category: null });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete document',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleViewDocument = async (doc, category) => {
    setViewerLoading(true);
    setIsViewerOpen(true);
    
    try {
      // Get presigned URL for viewing
      const viewResponse = await documentVerificationService.getDocumentViewUrl(
        docVerification._id,
        doc._id,
        category
      );
      const viewUrl = viewResponse.data?.data?.viewUrl || viewResponse.data?.viewUrl;
      
      setViewerDocument({
        ...doc,
        viewUrl: viewUrl || doc.fileUrl,
        category: category,
      });
    } catch (error) {
      console.error('Error getting view URL:', error);
      // Fallback to stored URL
      setViewerDocument({
        ...doc,
        viewUrl: doc.fileUrl,
        category: category,
      });
      toast({
        title: 'Warning',
        description: 'Using fallback URL to view document',
        status: 'warning',
        duration: 3000,
      });
    } finally {
      setViewerLoading(false);
    }
  };

  const closeViewer = () => {
    setIsViewerOpen(false);
    setViewerDocument(null);
  };

  const openStatusModal = (doc, category) => {
    // For reviewers reviewing documents with "submitted_for_review" status,
    // default to empty string so they must make a choice
    // For other statuses or non-reviewers, use the document's current status
    let initialStatus = doc.status;
    if (isReviewer && doc.status === 'submitted_for_review') {
      initialStatus = ''; // Force reviewer to select Accept or Reject
    }

    setStatusForm({
      status: initialStatus,
      comments: doc.comments || '',
      category: category,
      docId: doc._id,
      documentType: doc.documentType || '',
      fileName: doc.fileName || '',
    });
    onStatusOpen();
  };

  const canSubmitForReview = () => {
    if (!docVerification) {
      console.log('[Submit Button] No docVerification');
      return false;
    }
    // Just check if there are documents - no need to accept them first
    // The reviewer will accept/reject them after submission
    const personalCount = docVerification.documents?.personal?.length || 0;
    const professionalCount = docVerification.documents?.professional?.length || 0;
    const hasDocuments = personalCount > 0 || professionalCount > 0;

    console.log('[Submit Button] canSubmitForReview check:', {
      hasDocuments,
      personalCount,
      professionalCount,
      status: docVerification.verificationStatus,
      isRecruiter,
      isReviewer,
      documentsStructure: docVerification.documents
    });

    return hasDocuments;
  };

  const hasDocumentsToReview = () => {
    if (!docVerification) return false;
    return (
      docVerification.documents.personal.length > 0 ||
      docVerification.documents.professional.length > 0
    );
  };

  if (isLoading) {
    return (
      <Box p={4} textAlign="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box p={4}>
      {/* Read-only banner when flow is completed */}
      {isFlowCompleted && (
        <Alert status="success" mb={4} borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Document verification has been approved. This record is now read-only.
          </AlertDescription>
        </Alert>
      )}

      <HStack justify="space-between" mb={4} flexWrap="wrap">
        <Text fontSize="xl" fontWeight="bold">
          Document Verification - {candidateName || 'Candidate'}
        </Text>
        <HStack flexWrap="wrap">
          {/* Request Documents - Only visible to recruiters and superAdmin */}
          {(isRecruiter || isSuperAdmin) && !docVerification && !shouldDisableActions && (
            <Button
              colorScheme="blue"
              onClick={(e) => {
                e?.preventDefault?.();
                e?.stopPropagation?.();
                if (typeof onRequestOpen === 'function') {
                  onRequestOpen();
                } else if (typeof setIsRequestOpen === 'function') {
                  setIsRequestOpen(true);
                } else {
                  console.error('onRequestOpen is not a function:', typeof onRequestOpen);
                }
              }}
            >
              Request Documents
            </Button>
          )}
          {docVerification && (
            <>
              {/* Upload Document - Only visible to recruiter, hiring manager, and admin */}
              {isRecruiter && !shouldDisableActions && (
                <Button
                  colorScheme="green"
                  onClick={onUploadOpen}
                  leftIcon={<AddIcon />}
                  isDisabled={isLoading}
                  title="Upload a new document"
                >
                  Upload Document
                </Button>
              )}
              {/* Review Documents - Bulk review button for reviewers to approve/reject entire verification at once */}
              {/* Note: This is a legacy bulk review feature. Reviewers can also review individual documents using the "Review" button next to each document */}
              {isReviewer && !shouldDisableActions && (
                (docVerification.verificationStatus === 'pending_review' ||
                  docVerification.documents.personal.some(doc => doc.status === 'submitted_for_review') ||
                  docVerification.documents.professional.some(doc => doc.status === 'submitted_for_review')) && (
                  <Button colorScheme="orange" onClick={onReviewOpen} title="Bulk review: Approve or reject the entire document verification at once">
                    Review Documents
                  </Button>
                )
              )}
            </>
          )}
        </HStack>
      </HStack>

      {!docVerification ? (
        <Box p={8} textAlign="center" border="1px dashed" borderColor="gray.300" borderRadius="md">
          <Text color="gray.500">No document verification started yet.</Text>
          <Text color="gray.500" fontSize="sm" mt={2}>
            Click "Request Documents" to send an email to the candidate.
          </Text>
          {/* Upload Document button even when no docVerification exists yet */}
          {isRecruiter && !shouldDisableActions && (
            <Button
              colorScheme="green"
              onClick={onUploadOpen}
              leftIcon={<AddIcon />}
              mt={4}
              isDisabled={!applicationId}
              title={!applicationId ? 'Application ID is required' : 'Upload a document'}
            >
              Upload Document
            </Button>
          )}
        </Box>
      ) : (
        <VStack align="stretch" spacing={4}>
          {/* Overall Status */}
          <Box p={4} bg="gray.50" borderRadius="md">
            <HStack justify="space-between">
              <Text fontWeight="bold">Verification Status:</Text>
              <HStack spacing={2}>
                <Box
                  bg={getStatusBadgeStyle(docVerification.verificationStatus).color}
                  h="8px"
                  w="8px"
                  borderRadius="full"
                />
                <Text
                  as="span"
                  color={getStatusBadgeStyle(docVerification.verificationStatus).color}
                  style={{ color: getStatusBadgeStyle(docVerification.verificationStatus).color }}
                  fontWeight="600"
                >
                  {docVerification.verificationStatus.replace('_', ' ').toUpperCase()}
                </Text>
              </HStack>
            </HStack>
          </Box>

          {/* Document Folders */}
          <Accordion 
            allowMultiple 
            index={expandedAccordionIndex} 
            onChange={setExpandedAccordionIndex}
          >
            {/* Personal Documents */}
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left" fontWeight="bold">
                  Personal Documents ({docVerification.documents.personal.length})
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                {docVerification.documents.personal.length === 0 ? (
                  <Text color="gray.500" fontSize="sm">
                    No personal documents uploaded yet.
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={2}>
                    {docVerification.documents.personal.map((doc) => (
                      <Box
                        key={doc._id}
                        p={3}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                      >
                        <HStack justify="space-between">
                          <VStack align="start" spacing={1} flex={1}>
                            <Text fontWeight="medium">{doc.documentType}</Text>
                            <Text fontSize="sm" color="gray.600">
                              {doc.fileName}
                            </Text>
                            <HStack>
                              <Badge colorScheme={statusColors[doc.status]}>{formatStatus(doc.status)}</Badge>
                              {doc.comments && (
                                <Text fontSize="xs" color="gray.500">
                                  {doc.comments}
                                </Text>
                              )}
                            </HStack>
                            {doc.reviewedBy && (
                              <Text fontSize="xs" color="green.600" fontWeight="medium">
                                Reviewed by: {doc.reviewedBy?.firstName || ''} {doc.reviewedBy?.lastName || ''}
                                {doc.reviewedAt && ` on ${new Date(doc.reviewedAt).toLocaleDateString()}`}
                              </Text>
                            )}
                            {doc.lastUpdatedAt && (
                              <Text fontSize="xs" color="gray.400">
                                Last updated: {new Date(doc.lastUpdatedAt).toLocaleString()}
                              </Text>
                            )}
                          </VStack>
                          <HStack>
                            <IconButton
                              icon={<ViewIcon />}
                              size="sm"
                              colorScheme="blue"
                              onClick={() => handleViewDocument(doc, 'personal')}
                              title="View document"
                            />
                            <IconButton
                              icon={<DownloadIcon />}
                              size="sm"
                              onClick={async () => {
                                try {
                                  // Get presigned URL for downloading
                                  const viewResponse = await documentVerificationService.getDocumentViewUrl(
                                    docVerification._id,
                                    doc._id,
                                    'personal'
                                  );
                                  const viewUrl = viewResponse.data?.data?.viewUrl || viewResponse.data?.viewUrl;
                                  if (viewUrl) {
                                    window.open(viewUrl, '_blank');
                                  } else {
                                    // Fallback to stored URL
                                    window.open(doc.fileUrl, '_blank');
                                  }
                                } catch (error) {
                                  console.error('Error getting view URL:', error);
                                  // Fallback to stored URL
                                  window.open(doc.fileUrl, '_blank');
                                }
                              }}
                              title="Download document"
                            />
                            {/* Delete - Only visible to recruiters when NOT read-only/completed */}
                            {isRecruiter && !shouldDisableActions && (
                              <IconButton
                                icon={<DeleteIcon />}
                                size="sm"
                                colorScheme="red"
                                onClick={() => handleDeleteDocumentClick(doc._id, 'personal')}
                              />
                            )}
                            {/* Submit for Review - Only for pending documents, visible to recruiters, when NOT read-only/completed */}
                            {isRecruiter && doc.status === 'pending' && !shouldDisableActions && (
                              <Button
                                size="sm"
                                colorScheme="purple"
                                onClick={() => handleSubmitForReview(doc._id, 'personal')}
                                isDisabled={isLoading}
                              >
                                Submit for Review
                              </Button>
                            )}
                            {/* Review/Accept/Reject - Visible to reviewers for submitted documents, when NOT read-only/completed */}
                            {isReviewer && doc.status === 'submitted_for_review' && !shouldDisableActions && (
                              <Button
                                size="sm"
                                colorScheme="green"
                                onClick={() => openStatusModal(doc, 'personal')}
                                title="Review and accept/reject this document"
                              >
                                Review
                              </Button>
                            )}
                            {/* Update Status - Visible to recruiters and reviewers, when NOT read-only/completed */}
                            {(isRecruiter || isReviewer) && !shouldDisableActions && (
                              <Button
                                size="sm"
                                onClick={() => openStatusModal(doc, 'personal')}
                              >
                                Update Status
                              </Button>
                            )}
                          </HStack>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                )}
              </AccordionPanel>
            </AccordionItem>

            {/* Professional Documents */}
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left" fontWeight="bold">
                  Professional Documents ({docVerification.documents.professional.length})
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                {docVerification.documents.professional.length === 0 ? (
                  <Text color="gray.500" fontSize="sm">
                    No professional documents uploaded yet.
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={2}>
                    {docVerification.documents.professional.map((doc) => (
                      <Box
                        key={doc._id}
                        p={3}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                      >
                        <HStack justify="space-between">
                          <VStack align="start" spacing={1} flex={1}>
                            <Text fontWeight="medium">{doc.documentType}</Text>
                            <Text fontSize="sm" color="gray.600">
                              {doc.fileName}
                            </Text>
                            <HStack>
                              <Badge colorScheme={statusColors[doc.status]}>{formatStatus(doc.status)}</Badge>
                              {doc.comments && (
                                <Text fontSize="xs" color="gray.500">
                                  {doc.comments}
                                </Text>
                              )}
                            </HStack>
                            {doc.reviewedBy && (
                              <Text fontSize="xs" color="green.600" fontWeight="medium">
                                Reviewed by: {doc.reviewedBy?.firstName || ''} {doc.reviewedBy?.lastName || ''}
                                {doc.reviewedAt && ` on ${new Date(doc.reviewedAt).toLocaleDateString()}`}
                              </Text>
                            )}
                            {doc.lastUpdatedAt && (
                              <Text fontSize="xs" color="gray.400">
                                Last updated: {new Date(doc.lastUpdatedAt).toLocaleString()}
                              </Text>
                            )}
                          </VStack>
                          <HStack>
                            <IconButton
                              icon={<ViewIcon />}
                              size="sm"
                              colorScheme="blue"
                              onClick={() => handleViewDocument(doc, 'professional')}
                              title="View document"
                            />
                            <IconButton
                              icon={<DownloadIcon />}
                              size="sm"
                              onClick={async () => {
                                try {
                                  // Get presigned URL for downloading
                                  const viewResponse = await documentVerificationService.getDocumentViewUrl(
                                    docVerification._id,
                                    doc._id,
                                    'professional'
                                  );
                                  const viewUrl = viewResponse.data?.data?.viewUrl || viewResponse.data?.viewUrl;
                                  if (viewUrl) {
                                    window.open(viewUrl, '_blank');
                                  } else {
                                    // Fallback to stored URL
                                    window.open(doc.fileUrl, '_blank');
                                  }
                                } catch (error) {
                                  console.error('Error getting view URL:', error);
                                  // Fallback to stored URL
                                  window.open(doc.fileUrl, '_blank');
                                }
                              }}
                              title="Download document"
                            />
                            {/* Delete - Only visible to recruiters when NOT read-only/completed */}
                            {isRecruiter && !shouldDisableActions && (
                              <IconButton
                                icon={<DeleteIcon />}
                                size="sm"
                                colorScheme="red"
                                onClick={() => handleDeleteDocumentClick(doc._id, 'professional')}
                              />
                            )}
                            {/* Submit for Review - Only for pending documents, visible to recruiters, when NOT read-only/completed */}
                            {isRecruiter && doc.status === 'pending' && !shouldDisableActions && (
                              <Button
                                size="sm"
                                colorScheme="purple"
                                onClick={() => {
                                  console.log('[Submit for Review] Button clicked:', { docId: doc._id, category: 'professional', isRecruiter, docStatus: doc.status });
                                  handleSubmitForReview(doc._id, 'professional');
                                }}
                                isDisabled={isLoading}
                              >
                                Submit for Review
                              </Button>
                            )}
                            {/* Review/Accept/Reject - Visible to reviewers for submitted documents, when NOT read-only/completed */}
                            {isReviewer && doc.status === 'submitted_for_review' && !shouldDisableActions && (
                              <Button
                                size="sm"
                                colorScheme="green"
                                onClick={() => openStatusModal(doc, 'professional')}
                                title="Review and accept/reject this document"
                              >
                                Review
                              </Button>
                            )}
                            {/* Update Status - Visible to recruiters and reviewers, when NOT read-only/completed */}
                            {(isRecruiter || isReviewer) && !shouldDisableActions && (
                              <Button
                                size="sm"
                                onClick={() => openStatusModal(doc, 'professional')}
                              >
                                Update Status
                              </Button>
                            )}
                          </HStack>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                )}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>

        </VStack>
      )}

      {/* Request Documents Modal */}
      <Modal isOpen={isRequestOpen} onClose={onRequestClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxW="1200px" w="90vw">
          <ModalHeader>Request Documents</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Email Subject</FormLabel>
                <Input
                  value={requestForm.emailSubject}
                  onChange={(e) =>
                    setRequestForm({ ...requestForm, emailSubject: e.target.value })
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>Email Content</FormLabel>
                <TextEditor
                  toolbarId="document-verification-tab-email-content"
                  value={requestForm.emailContent || ''}
                  handleChange={(value) => {
                    const cleanedValue = value === "<p><br></p>" ? "" : value;
                    setRequestForm({ ...requestForm, emailContent: cleanedValue });
                  }}
                />
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Available placeholders: [Candidate Name], [Job Role], [YourName], [Deadline Date]
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>Deadline Date (Optional)</FormLabel>
                <CustomDatePicker
                  selectedDate={requestForm.deadlineDate}
                  onChange={(date) => setRequestForm({ ...requestForm, deadlineDate: date })}
                  placeholderText="Select deadline date"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  This will replace [Deadline Date] placeholder in the email content
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onRequestClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleRequestDocuments}
              isLoading={isLoading}
              loadingText="Sending..."
            >
              Send Request
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Upload Document Modal */}
      <Modal isOpen={isUploadOpen} onClose={onUploadClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Upload Document</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Category</FormLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="personal">Personal</option>
                  <option value="professional">Professional</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Document Type</FormLabel>
                <Select
                  value={selectedDocType}
                  onChange={(e) => setSelectedDocType(e.target.value)}
                  placeholder="Select document type"
                >
                  {documentTypes[selectedCategory].map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>File</FormLabel>
                <Input
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  disabled={isUploading}
                />
                {isUploading && (
                  <Box mt={2}>
                    <Progress value={uploadProgress} colorScheme="blue" />
                    <Text fontSize="sm" mt={1} textAlign="center">
                      Uploading... {uploadProgress}%
                    </Text>
                  </Box>
                )}
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onUploadClose} isDisabled={isUploading}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUploadDocument}
              isLoading={isUploading}
              loadingText="Uploading"
            >
              Upload
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Update Status Modal */}
      <Modal isOpen={isStatusOpen} onClose={onStatusClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isReviewer ? 'Review Document' : 'Update Document Status'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {/* Document Information */}
              {statusForm.documentType && (
                <Box w="100%" p={3} bg="gray.50" borderRadius="md">
                  <Text fontSize="sm" fontWeight="bold" mb={1}>
                    Document Being Reviewed:
                  </Text>
                  <Text fontSize="md" fontWeight="medium" color="blue.600">
                    {statusForm.documentType}
                  </Text>
                  {statusForm.fileName && (
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      File: {statusForm.fileName}
                    </Text>
                  )}
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Category: {statusForm.category === 'personal' ? 'Personal' : 'Professional'}
                  </Text>
                </Box>
              )}
              <FormControl isRequired>
                <FormLabel>Status</FormLabel>
                <Select
                  value={statusForm.status || ''}
                  onChange={(e) => {
                    console.log('[Status Select] Changed to:', e.target.value, 'Current statusForm:', statusForm.status);
                    setStatusForm({ ...statusForm, status: e.target.value });
                  }}
                  placeholder={isReviewer ? "Select Accept or Reject" : "Select status"}
                >
                  {isReviewer ? (
                    <>
                      <option value="accepted">Accept</option>
                      <option value="rejected">Reject</option>
                    </>
                  ) : (
                    <>
                      <option value="accepted">Accept</option>
                      <option value="rejected">Reject</option>
                      <option value="needs_info">Needs More Information</option>
                      <option value="pending">Pending</option>
                    </>
                  )}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Comments</FormLabel>
                <Textarea
                  value={statusForm.comments}
                  onChange={(e) => setStatusForm({ ...statusForm, comments: e.target.value })}
                  rows={4}
                  placeholder="Add comments if needed..."
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onStatusClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleUpdateStatus}>
              Update
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Review Documents Modal */}
      <Modal isOpen={isReviewOpen} onClose={onReviewClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Review Documents</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Review Type</FormLabel>
                <Select
                  value={reviewForm.reviewType}
                  onChange={(e) => setReviewForm({ ...reviewForm, reviewType: e.target.value })}
                >
                  <option value="admin">Admin</option>
                  <option value="finance">Finance</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={reviewForm.status}
                  onChange={(e) => setReviewForm({ ...reviewForm, status: e.target.value })}
                >
                  <option value="approved">Approve</option>
                  <option value="rejected">Reject</option>
                  <option value="on_hold">Keep on Hold</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Comments</FormLabel>
                <Textarea
                  value={reviewForm.comments}
                  onChange={(e) => setReviewForm({ ...reviewForm, comments: e.target.value })}
                  rows={4}
                  placeholder="Add review comments..."
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onReviewClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleReview}>
              Submit Review
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Document Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Document</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete this document? This action cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={() => setIsDeleteConfirmOpen(false)} variant="outline">
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleDeleteDocument}>
              Yes, Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Document Viewer Modal */}
      <Modal isOpen={isViewerOpen} onClose={closeViewer} size="6xl" isCentered>
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>
            <HStack justify="space-between" pr={8}>
              <VStack align="start" spacing={0}>
                <Text>{viewerDocument?.documentType || 'Document'}</Text>
                <HStack spacing={2}>
                  <Badge colorScheme={viewerDocument?.category === 'personal' ? 'purple' : 'teal'} fontSize="xs">
                    {viewerDocument?.category === 'personal' ? 'Personal' : 'Professional'}
                  </Badge>
                  <Text fontSize="sm" color="gray.600" fontWeight="normal">
                    {viewerDocument?.fileName}
                  </Text>
                </HStack>
              </VStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0}>
            {viewerLoading ? (
              <Flex justify="center" align="center" h="500px">
                <Spinner size="xl" />
              </Flex>
            ) : viewerDocument?.viewUrl ? (
              <Box h="calc(83vh - 140px)" w="100%" overflow="hidden">
                {viewerDocument.fileName?.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={viewerDocument.viewUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                    }}
                    title={viewerDocument.documentType}
                  />
                ) : (
                  <Box h="100%" w="100%" display="flex" justifyContent="center" alignItems="center" bg="gray.50">
                    <img
                      src={viewerDocument.viewUrl}
                      alt={viewerDocument.documentType}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <Flex justify="center" align="center" h="500px">
                <Text color="gray.500">Unable to load document preview</Text>
              </Flex>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              leftIcon={<DownloadIcon />}
              colorScheme="blue"
              mr={3}
              onClick={() => {
                if (viewerDocument?.viewUrl) {
                  window.open(viewerDocument.viewUrl, '_blank');
                }
              }}
            >
              Download
            </Button>
            <Button variant="outline" onClick={closeViewer}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DocumentVerificationTab;
